import React, {useContext, useEffect, useRef, useState} from 'react';
import UniversalProfileImage from 'components/universal/UniversalProfileImage';
import {UserContext} from 'components/user/UserContext';
import AddMoneyPopupWrapper from 'components/donate/AddMoneyPopupWrapper';
import {usePopup} from 'popup/PopupContext';
import {pinCommentRequest, replyCommentPictureRequest} from 'util/api';
import itemTypeToEmoji from 'util/itemTypeToEmoji';
import ReactionListPopup from 'components/pictures/detail/ReactionListPopup';
import TimeAgoCompact from 'util/TimeAgoCompact';
import CommentInputArea from 'components/pictures/detail/CommentInputArea';
import ThreeDotOptions from 'components/universal/ThreeDotOptions';
import ReportMessagePopup from 'components/chat/ReportMessagePopup';
import PinIcon from 'util/svg/nicebutton/PinIcon';
import {useLocation, useNavigate} from 'react-router-dom';
import PictureCommentAddReaction from 'components/pictures/detail/PictureCommentAddReaction';
import ReplyArrowSvg from 'util/svg/ReplyArrowSvg';
import SignInPopup from 'components/login/SignInPopup';
import {CustomError} from 'util/apiHelper';

const PictureComment = ({
                            comment,
                            isHighlighted,
                            pictureId,
                            allowPin,
                            highlightedResponseId
                        }) => {
    const [emojiSum, setEmojiSum] = useState('');
    const [showReplyToCommentBar, setShowReplyToCommentBar] = useState(false);
    const {user} = useContext(UserContext);
    const {openPopup} = usePopup();
    const [shownReplies, setShownReplies] = useState(comment.commentResponses.filter((response) => response.isOpened));
    const [remainingReplies, setRemainingReplies] = useState(comment.commentResponses.length - shownReplies.length);
    const replyInputRef = useRef(null);
    const hasScrolledRef = useRef(false);
    const navigate = useNavigate();
    const location = useLocation();
    const currentCommentRef = useRef(null);
    const commentReplyRef = useRef(null);
    const [isReplyHovered, setIsReplyHovered] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [copyToast, setCopyToast] = useState({visible: false, message: '', status: 'success'});
    const copyToastTimeoutRef = useRef(null);
    const clearToastTimeoutRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                currentCommentRef.current && !currentCommentRef.current.contains(event.target) &&
                commentReplyRef.current && !commentReplyRef.current.contains(event.target)
            ) {
                setShowReplyToCommentBar(false);
                document.removeEventListener('mousedown', handleClickOutside);
            }
        };

        if (showReplyToCommentBar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showReplyToCommentBar]);

    useEffect(() => {
        return () => {
            if (copyToastTimeoutRef.current) {
                clearTimeout(copyToastTimeoutRef.current);
            }
            if (clearToastTimeoutRef.current) {
                clearTimeout(clearToastTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!highlightedResponseId || hasScrolledRef.current) {
            return;
        }
        hasScrolledRef.current = true;
        const element = document.getElementById('comment_' + highlightedResponseId);
        if (element) {
            element.scrollIntoView({behavior: 'smooth'});
        } else {
            const commentToAdd = comment.commentResponses.find((responseComment) => responseComment.id === highlightedResponseId);
            if (commentToAdd) {
                setShownReplies((prevVisibleComments) => {
                    if (!prevVisibleComments.some((visibleComment) => visibleComment.id === highlightedResponseId)) {
                        commentToAdd.order = 0;
                        return [...prevVisibleComments, commentToAdd];
                    }
                    return prevVisibleComments;
                });
                const scrollToElement = () => {
                    const newElement = document.getElementById('comment_' + highlightedResponseId);
                    if (newElement) {
                        newElement.scrollIntoView({behavior: 'smooth'});
                    }
                };
                setTimeout(scrollToElement, 100);
            }
        }
    }, [comment.commentResponses, highlightedResponseId]);

    useEffect(() => {
        const openedComments = comment.commentResponses
            .filter((filterComment) => filterComment.order || filterComment.order === 0)
            .sort((a, b) => a.order - b.order);
        setShownReplies(openedComments);
        setRemainingReplies(comment.commentResponses.length - openedComments.length);
    }, [comment.commentResponses]);

    const showCopyToast = (message, status = 'success') => {
        setCopyToast({visible: true, message, status});
        if (copyToastTimeoutRef.current) {
            clearTimeout(copyToastTimeoutRef.current);
        }
        if (clearToastTimeoutRef.current) {
            clearTimeout(clearToastTimeoutRef.current);
        }
        copyToastTimeoutRef.current = setTimeout(() => {
            setCopyToast((prev) => ({...prev, visible: false}));
            clearToastTimeoutRef.current = setTimeout(() => {
                setCopyToast({visible: false, message: '', status});
            }, 280);
        }, 2800);
    };

    const copyCommentLink = () => {
        const commentUrl = `${window.location.origin}${location.pathname}${location.search}#${comment.id}`;
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Clipboard copy timed out')), 3000);
        });

        return Promise.race([
            navigator.clipboard.writeText(commentUrl),
            timeoutPromise
        ])
            .then(() => {
                showCopyToast('Link copied to clipboard', 'success');
                return true;
            })
            .catch((error) => {
                console.error('Error copying comment link:', error);
                showCopyToast('Failed to copy link', 'error');
                return true;
            });
    };

    const optionsClick = (element) => {
        if (element === 'share') {
            return copyCommentLink();
        }

        if (!user.isLoggedIn) {
            openPopup(<SignInPopup/>);
            return true;
        }

        if (element === 'report') {
            openPopup(<ReportMessagePopup msg={comment.comment} type={'comment'}/>);
        }
        if (element === 'pin') {
            if (user.balance < 1) {
                openPopup(<AddMoneyPopupWrapper initialAmount={1}/>);
                return true;
            }
            pinCommentRequest(comment.id, user.balance).catch(async (error) => {
                if (error instanceof CustomError && error.status === 400) {
                    try {
                        const errorBody = JSON.parse(await error.body);
                        if (Array.isArray(errorBody) && errorBody[0]?.errorText) {
                            alert(errorBody[0].errorText);
                            return;
                        }
                    } catch (parseError) {
                        console.error('Failed to parse pin error', parseError);
                    }
                }
                alert('Failed to pin comment. Please try again.');
            });
        }
        return true;
    };

    useEffect(() => {
        console.log('Updating emoji sum');
        if (comment.reactions && Array.isArray(comment.reactions)) {
            const totalReactionPrice = comment.reactions.reduce((sum, reaction) => sum + reaction.price, 0);
            const roundedPrice = (totalReactionPrice / 100).toFixed(2);
            const dashedRoundedPrice = roundedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            setEmojiSum(dashedRoundedPrice);
        }
    }, [comment.reactions]);

    const onReactionsClick = () => {
        openPopup(<ReactionListPopup comment={comment}/>);
    };

    const replyClicked = () => {
        if (!user.isLoggedIn) {
            openPopup(<SignInPopup/>);
            return;
        }

        setShowReplyToCommentBar((prev) => {
            if (!prev) {
                setTimeout(() => {
                    replyInputRef.current?.focus();
                }, 0);
            }
            return !prev;
        });
    };

    const commentPicture = (input) => {
        replyCommentPictureRequest(pictureId, comment.id, input, user.balance).then(() => {
            setShowReplyToCommentBar(false);
        });
    };

    const showMoreReplies = () => {
        const currentCount = shownReplies.length;
        const newVisibleCount = currentCount + 10;
        const newVisibleComments = comment.commentResponses.slice(0, newVisibleCount);

        newVisibleComments.forEach((responseComment, index) => {
            if (responseComment.order == null) {
                responseComment.order = index + currentCount + 1;
            }
        });

        setShownReplies(newVisibleComments);
        setRemainingReplies(comment.commentResponses.length - newVisibleComments.length);
    };

    const authorClicked = () => {
        navigate(`/profile/${comment.authorId}`);
    };

    const shouldShowOptionsTrigger = isHovered || isOptionsOpen || !!copyToast.message;

    return (
        <div>
            <div
                className={`comment-item 
                ${isHighlighted ? 'highlight' : ''} 
                ${comment.isPinned ? 'pinned' : ''} 
                ${remainingReplies > 0 && shownReplies.length === 0 ? 'bottomless' : ''}
                `}
                ref={currentCommentRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {comment.isPinned &&
                    <div className="comment-pinned">
                        <PinIcon size={14} color={'black'}/>
                        <div>Pinned by author</div>
                    </div>
                }
                <UniversalProfileImage url={comment.authorProfileImgId} cssClass={'comment-profile-picture'}
                                       onClick={authorClicked}/>
                <div className="comment-content-wrapper">
                    <div className="comment-content">
                        <span className="comment-author" onClick={authorClicked}>{comment.authorName}:</span>
                        <p className="comment-text">{comment.comment}</p>
                    </div>
                    <div className="comment-options">
                        {comment.depth === 1 && (
                            <div className={`comment-reply ${showReplyToCommentBar || isReplyHovered ? 'opened' : ''}`}
                                 onClick={replyClicked}
                                 onMouseEnter={() => setIsReplyHovered(true)}
                                 onMouseLeave={() => setIsReplyHovered(false)}
                            >
                                <ReplyArrowSvg size={10}
                                               color={showReplyToCommentBar || isReplyHovered ? 'black' : 'gray'}/> reply
                            </div>
                        )}
                        <PictureCommentAddReaction comment={comment}/>
                        {comment.firstReaction && (
                            <div className="comment-reactions" onClick={onReactionsClick}>
                                {itemTypeToEmoji[comment.firstReaction].emoji}{comment.secondReaction ? itemTypeToEmoji[comment.secondReaction].emoji : ''} {emojiSum}
                            </div>
                        )}
                        <TimeAgoCompact timestamp={comment.commentedOn} cssName={'picture-comment-time-ago'}/>
                        <div className="comment-options-menu-holder">
                            {shouldShowOptionsTrigger &&
                                <ThreeDotOptions
                                    elements={{
                                        share: 'Share',
                                        ...(allowPin && !comment.isPinned && {pin: 'Pin (1\u00A2)'}),
                                        report: 'Report',
                                    }}
                                    onElementClick={optionsClick}
                                    onOpenStateChange={setIsOptionsOpen}
                                    isBottomMessage={true}
                                />
                            }
                            {copyToast.message && (
                                <span
                                    className={`comment-share-toast ${copyToast.visible ? 'visible' : ''} ${copyToast.status}`}>
                                    {copyToast.message}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {remainingReplies > 0 && shownReplies.length === 0 && (
                <div className="comment-inlined-show-replies-div" onClick={showMoreReplies}>
                    {`Show ${remainingReplies} ${(remainingReplies === 1) ? 'reply' : 'replies'}`}
                </div>
            )}
            {showReplyToCommentBar && (
                <div style={{padding: '0 20px 10px 50px'}} ref={commentReplyRef}>
                    <CommentInputArea
                        ref={replyInputRef}
                        isImageMy={false}
                        postChatMessage={commentPicture}
                        inputPlaceholder={'Write a response...'}/>
                </div>
            )}

            {shownReplies.length > 0 && (
                <div className="comment-responses">
                    {shownReplies.map((response) => (
                        <div key={response.id} id={'comment_' + response.id} style={{marginLeft: '50px'}}>
                            <PictureComment
                                key={response.id}
                                comment={response}
                                isHighlighted={highlightedResponseId === response.id}
                                pictureId={pictureId}
                                allowPin={false}
                            />
                        </div>
                    ))}
                </div>
            )}

            {remainingReplies > 0 && shownReplies.length > 0 && (
                <div style={{marginLeft: '50px'}} className="comment-item">
                    <div className="comment-show-replies-div" onClick={showMoreReplies}>
                        {`Show ${remainingReplies} more ${(remainingReplies === 1) ? 'reply' : 'replies'}`}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PictureComment;
